import { LoadingSpinner, Modal } from "@msys/ui";
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { BrandPreferenceLineFragment } from "../BrandPreferences.generated.js";
import {
  BRAND_PREFERENCE_LINE_LIMIT,
  BrandLineAvailabilityScope,
  lineName,
  useAvailableBrandLineOptions,
} from "../brandPreferenceOptions.js";

interface Props {
  brand: { id: string; title: string };
  /** The preference's lines in order; empty for the whole brand. */
  lines: readonly BrandPreferenceLineFragment[];
  /** Where the offered lines must be available. */
  availableIn: BrandLineAvailabilityScope;
  handleClose: () => void;
  onSave: (brandLineIds: string[]) => void;
}

/** Narrows one brand preference to an ordered set of the brand's lines, or widens it again. */
export function EditBrandLinesModal({
  brand,
  lines,
  availableIn,
  handleClose,
  onSave,
}: Props) {
  const { t } = useTranslate(["Product", "Global"]);
  const [selected, setSelected] =
    React.useState<readonly BrandPreferenceLineFragment[]>(lines);
  const available = useAvailableBrandLineOptions(brand, availableIn);

  const selectedIds = new Set(selected.map(line => line.id));
  const atLimit = selected.length >= BRAND_PREFERENCE_LINE_LIMIT;
  const toggle = (line: BrandPreferenceLineFragment) =>
    setSelected(current =>
      current.some(candidate => candidate.id === line.id)
        ? current.filter(candidate => candidate.id !== line.id)
        : [...current, line]
    );
  const move = (index: number, by: -1 | 1) =>
    setSelected(current => {
      const target = index + by;
      const moved = current[index];
      if (moved === undefined || target < 0 || target >= current.length)
        return current;
      const rest = current.filter((_, position) => position !== index);
      return [...rest.slice(0, target), moved, ...rest.slice(target)];
    });

  return (
    <Modal
      title={t("Edit lines of {brand}", { ns: "Product", brand: brand.title })}
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Save", { ns: "Global" }),
          handleClick: () => {
            onSave(selected.map(line => line.id));
            handleClose();
          },
        },
      ]}
    >
      <Stack direction="column" spacing={2}>
        <Typography variant="body2" sx={{ color: "grey.600" }}>
          {t(
            "Search ranks the selected lines in this order. A main line also covers its sub-lines. Select no line to prefer the whole brand.",
            { ns: "Product" }
          )}
        </Typography>
        {selected.length === 0 ? (
          <Typography variant="body2">
            {t("The whole brand is preferred.", { ns: "Product" })}
          </Typography>
        ) : (
          <Stack component="ol" direction="column" sx={{ m: 0, pl: 0 }}>
            {selected.map((line, index) => (
              <Stack
                component="li"
                key={line.id}
                direction="row"
                spacing={1}
                sx={{ alignItems: "center", listStyle: "none" }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {`${index + 1}. ${lineName(line)}`}
                  {line.deletedAt ? (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "warning.main", ml: 1 }}
                    >
                      {t("Deleted in the catalogue", { ns: "Product" })}
                    </Typography>
                  ) : null}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={`${lineName(line)}: ${t("Move up", { ns: "Product" })}`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`${lineName(line)}: ${t("Move down", { ns: "Product" })}`}
                  disabled={index === selected.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`${lineName(line)}: ${t("Remove", { ns: "Global" })}`}
                  onClick={() => toggle(line)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
        <Box sx={{ maxHeight: "20em", overflowY: "auto" }}>
          {available.loading ? (
            <LoadingSpinner />
          ) : available.failed ? (
            <Typography variant="body2" sx={{ color: "grey.600" }}>
              {t("Something went wrong. Please try again.", { ns: "Global" })}
            </Typography>
          ) : available.options.length === 0 ? (
            <Typography variant="body2" sx={{ color: "grey.600" }}>
              {t("No line of this brand is available in the catalogue.", {
                ns: "Product",
              })}
            </Typography>
          ) : (
            available.options.map(line => (
              <FormControlLabel
                key={line.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(line.id)}
                    disabled={atLimit && !selectedIds.has(line.id)}
                    onChange={() => toggle(line)}
                    slotProps={{ input: { "aria-label": lineName(line) } }}
                  />
                }
                label={
                  <Stack direction="column">
                    <Typography variant="body2">{lineName(line)}</Typography>
                    {line.mainBrandLine ? null : (
                      <Typography variant="caption" sx={{ color: "grey.600" }}>
                        {t("Also covers its sub-lines.", { ns: "Product" })}
                      </Typography>
                    )}
                  </Stack>
                }
                sx={{ ml: 0, mr: 0, width: "100%" }}
              />
            ))
          )}
        </Box>
      </Stack>
    </Modal>
  );
}
